import { Modal, Paper, Grid, Select, MenuItem, Button, FormControl, InputLabel, Alert } from "@mui/material";
import { useCreateRecord } from "../../../api/useCreateRecord";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { renderFieldInput } from "../../../components/fieldsInputs/renderFieldInput";
import { useFields } from "../../../api/useFields";
import { findField } from "../../../utils/findField";
import { useContext } from "react";
import { StaffWorkLogsContext } from "../StaffWorkLogsDetail";
import { Controller } from "react-hook-form";
import { useStore } from "../../../store/store";
import { ClinicsDropdown } from "../../../components/general-dropdowns/clinics-dropdown/ClinicsDropdown";
import { ROLES } from "../../../utils/constants";
import { timeOffTypes } from "../../../settings-screens/schedules/utils/scheduleUtils";
import { useQueryClient } from "@tanstack/react-query";
import { sortBy } from "lodash";
import { isSameDay } from "date-fns";
import { checkTimeOffAgainstPlanned } from "../utils/calculations";

const SICK_ABSENCE_TYPES = ["sick_leave", "missed_justified", "missed_not_justified"];

export const WorkLogModal = () => {
    const { workLogForm, setIsModalOpen, isModalOpen, isCurrentUserAdmin, currentUser, workLogs, staffAgreement } =
        useContext(StaffWorkLogsContext);

    const { notifications } = useStore();
    const queryClient = useQueryClient();
    const createRecord = useCreateRecord();
    const updateRecord = useUpdateRecord({ skipOptimisticUpdate: true });

    const { formatedFields, status: fieldsStatus } = useFields("schedules");
    const startDateField = findField(formatedFields, "start_date");
    const endDateField = findField(formatedFields, "end_date");
    const noteField = findField(formatedFields, "notes");
    const timeOffType = workLogForm.watch("type");
    const startValue = workLogForm.watch("start_date");
    const endValue = workLogForm.watch("end_date");

    const weeklyWorkHoursSchedule = sortBy(staffAgreement?.staff_weekly_hours || [], (entry: any) => entry.date_start);

    const timeOffHoursWarning = (() => {
        if (!SICK_ABSENCE_TYPES.includes(timeOffType) || !startValue || !endValue) return null;
        const start = startValue instanceof Date ? startValue : new Date(startValue);
        const end = endValue instanceof Date ? endValue : new Date(endValue);
        const enteredHours = (end.getTime() - start.getTime()) / 3600000;
        if (!(enteredHours > 0)) return null;
        const { exceeds, plannedHours } = checkTimeOffAgainstPlanned({ enteredHours, date: start, workLogs, weeklyWorkHoursSchedule });
        return exceeds ? { enteredHours, plannedHours } : null;
    })();

    const managerRoles = [ROLES.neurofeedback_director, ROLES.reception_director, ROLES.technical_director, ROLES.human_resources_manager, ROLES.tech_admin, ROLES.marketing_manager];

    const isCurrentUserAdminOrManager = currentUser && (isCurrentUserAdmin || managerRoles.includes(currentUser?.role.id));

    const handleSave = async (data: any) => {
        const entity = data.type === "schedules" ? "schedules" : "time_off";

        const startDateObject = data.start_date instanceof Date ? data.start_date : new Date(data.start_date);
        const endDateObject = data.end_date instanceof Date ? data.end_date : new Date(data.end_date);

        // Time off is entered one day at a time (the hours are credited per day) — reject multi-day spans.
        if (entity === "time_off" && !isSameDay(startDateObject, endDateObject)) {
            notifications.setNotification({
                isOpen: true,
                message: "Ausências têm de ser de um só dia — crie um registo por cada dia.",
                severity: "error",
            });
            return;
        }

        const startDate = data.start_date instanceof Date ? data.start_date.toISOString() : data.start_date;
        const endDate = data.end_date instanceof Date ? data.end_date.toISOString() : data.end_date;

        try {
            if (data.id) {
                await updateRecord.mutateAsync({
                    entity,
                    id: data.id,
                    data: {
                        start_date: startDate,
                        end_date: endDate,
                        user: data.user,
                        notes: data.notes,
                        type: entity === "time_off" ? data.type : undefined,
                        clinic_id: data.clinic_id,
                    },
                });
            } else {
                await createRecord.mutateAsync({
                    entity,
                    data: {
                        start_date: startDate,
                        end_date: endDate,
                        user: data.user,
                        notes: data.notes,
                        type: entity === "time_off" ? data.type : undefined,
                        clinic_id: data.clinic_id,
                    },
                })
      
                await queryClient.refetchQueries({
                    predicate: (query: any) => query.queryKey[0].includes(`schedules-records`) || query.queryKey[0].includes(`time_off-records`)
                });

            }
            await queryClient.invalidateQueries({
                predicate: (query: any) => query.queryKey[0].includes(`worked-vs-expected-hours`) 
            });
            workLogForm.reset();
            notifications.setNotification({
                isOpen: true,
                message: "Registo guardado com sucesso",
                severity: "success",
            });
            setIsModalOpen(false);
        } catch (error: any) {
            const errorMessage = error.response.data.errors[0].message;
            notifications.setNotification({
                isOpen: true,
                message: errorMessage,
                severity: "error",
            });
        }
    };

    const types = isCurrentUserAdminOrManager ? timeOffTypes : [{ label: "Horas", value: "schedules" }];

    if (fieldsStatus === "loading") return null;

    return (
        <Modal
            open={isModalOpen}
            onClose={() => {
                workLogForm.reset();
                setIsModalOpen(false);
            }}
        >
            <Paper sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 400, p: 4 }}>
                <form onSubmit={workLogForm.handleSubmit(handleSave)}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <ClinicsDropdown entityForm={workLogForm} fieldName="clinic_id" />
                        </Grid>
                        <Grid item xs={12}>
                            {startDateField && renderFieldInput(startDateField, null, "group", workLogForm)}
                        </Grid>
                        <Grid item xs={12}>
                            {endDateField && renderFieldInput(endDateField, null, "group", workLogForm)}
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="entry-type-label">Tipo de Registo</InputLabel>
                                <Controller
                                    name="type"
                                    control={workLogForm.control}
                                    render={({ field }) => {
                                        return (
                                            <Select
                                                labelId="entry-type-label"
                                                label="Tipo de Registo"
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                fullWidth
                                            >
                                                {types.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        );
                                    }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            {noteField && renderFieldInput(noteField, null, "group", workLogForm)}
                        </Grid>
                    </Grid>
                    {timeOffHoursWarning && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            As horas inseridas ({timeOffHoursWarning.enteredHours.toFixed(1)}h) excedem as horas previstas para este
                            dia ({timeOffHoursWarning.plannedHours.toFixed(1)}h). Pode guardar à mesma.
                        </Alert>
                    )}
                    <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}>
                        Guardar
                    </Button>
                </form>
            </Paper>
        </Modal>
    );
};
